import React, { useState } from 'react';
import PropTypes from "prop-types";
import { EuiErrorBoundary } from '../../../../src/components/error_boundary';
import { EuiText } from '../../../../src/components/text';
import { EuiSwitch } from '../../../../src/components/form';
import { slugify } from '../../../../src/services/string/slugify'; // @ts-ignore Not TS yet

import playgroundService from '../../services/playground/playground';
import { GuideSectionExample } from './guide_section_parts/guide_section_example';
import { GuideSectionExampleText } from './guide_section_parts/guide_section_text';
import { GuideSectionExampleTabs } from './guide_section_parts/guide_section_tabs';
import { GuideSectionPropsDescription } from './guide_section_parts/guide_section_props_description';
export var GuideSectionCodeTypesMap = {
  JS: {
    name: 'demoJS',
    displayName: 'Demo JS'
  },
  SNIPPET: {
    name: 'snippet',
    displayName: 'Snippet'
  },
  PROPS: {
    name: 'props',
    displayName: 'Props'
  }
};
export var GuideSection = ({
  id,
  title,
  text,
  demo,
  source = [],
  props = {},
  playground,
  ghostBackground,
  wrapText = true,
  demoPanelProps,
  snippet
}) => {
  const [renderingPlayground, setRenderingPlayground] = useState(false);

  const renderTabs = () => {
    const hasSnippet = !!snippet; // Don't duplicate in case this function is run multiple times

    if (hasSnippet && !source.find(tab => tab.name === 'snippet')) {
      source.push({ ...GuideSectionCodeTypesMap.SNIPPET,
        snippets: snippet
      });
    }

    const hasPropsTabAlready = source.find(tab => tab.name === 'props');

    if (Object.keys(props).length && !hasPropsTabAlready // Don't duplicate in case this function is run multiple times
    ) {
        source.push({ ...GuideSectionCodeTypesMap.PROPS,
          props: props
        });
      }

    const tabs = [];

    if (source) {
      source.map(source => {
        // Forever skipping the HTML tab
        if (source.type === 'HTML') return;
        tabs.push({ // @ts-ignore Complicated
          ...GuideSectionCodeTypesMap[source.type],
          // Make sure the `name` is unique in case there are multiple source languages
          name: source.displayName ? slugify(source.displayName) : // @ts-ignore Complicated
          GuideSectionCodeTypesMap[source.type].name,
          disabled: renderingPlayground,
          ...source
        });
      });
    }

    return tabs.length ? <GuideSectionExampleTabs tabs={tabs} rightSideControl={renderPlaygroundToggle()} /> : undefined;
  };

  const renderPlaygroundToggle = () => {
    const isPlaygroundUnsupported = typeof window !== 'undefined' && typeof document !== 'undefined' && !!window.MSInputMethodContext && // @ts-ignore doesn't exist?
    !!document.documentMode;

    if (!isPlaygroundUnsupported && !!playground) {
      return <EuiSwitch onChange={() => {
        setRenderingPlayground(rendering => !rendering);
      }} checked={renderingPlayground} compressed label={<EuiText size="xs">
              <strong>Playground</strong>
            </EuiText>} />;
    }
  };

  const renderPlayground = () => {
    const {
      config,
      setGhostBackground,
      playgroundClassName
    } = playground();
    const description = <GuideSectionPropsDescription componentName={config.componentName} component={config.scope[config.componentName]} />;
    return playgroundService({
      config,
      setGhostBackground,
      playgroundClassName,
      playgroundToggle: renderPlaygroundToggle(),
      tabs: renderTabs(),
      description
    });
  };

  return <div className="guideSection" id={id}>
      <GuideSectionExampleText title={title} wrapText={wrapText}>
        {text}
      </GuideSectionExampleText>

      {renderingPlayground && renderPlayground()}
      {!renderingPlayground && demo && <GuideSectionExample example={<EuiErrorBoundary>{demo}</EuiErrorBoundary>} tabs={renderTabs()} ghostBackground={ghostBackground} demoPanelProps={demoPanelProps} />}
    </div>;
};
GuideSection.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  text: PropTypes.node,
  source: PropTypes.arrayOf(PropTypes.any.isRequired),
  demo: PropTypes.node,
  demoPanelProps: PropTypes.shape({
    /**
       * Padding for all four sides
       */
    paddingSize: PropTypes.any,

    /**
       * When true the panel will grow in height to match `EuiFlexItem`
       */
    grow: PropTypes.bool,
    panelRef: PropTypes.any,

    /**
       * Background color of the panel;
       * Usually a lightened form of the brand colors
       */
    color: PropTypes.any,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string
  }),
  props: PropTypes.any,
  playground: PropTypes.any,
  ghostBackground: PropTypes.bool,
  wrapText: PropTypes.bool,
  snippet: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.arrayOf(PropTypes.string.isRequired).isRequired])
};

try {
  GuideSection.__docgenInfo = {
    description: '',
    displayName: 'GuideSection',
    methods: [],
    props: {
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      title: {
        defaultValue: null,
        description: '',
        name: 'title',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      text: {
        defaultValue: null,
        description: '',
        name: 'text',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      source: {
        defaultValue: {
          value: '[]'
        },
        description: '',
        name: 'source',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        required: false,
        type: {
          name: 'any[]'
        }
      },
      demo: {
        defaultValue: null,
        description: '',
        name: 'demo',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      demoPanelProps: {
        defaultValue: null,
        description: '',
        name: 'demoPanelProps',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        required: false,
        type: {
          name: '_EuiSplitPanelInnerProps'
        }
      },
      props: {
        defaultValue: {
          value: '{}'
        },
        description: '',
        name: 'props',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        required: false,
        type: {
          name: 'object'
        }
      },
      playground: {
        defaultValue: null,
        description: '',
        name: 'playground',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        required: false,
        type: {
          name: 'any'
        }
      },
      ghostBackground: {
        defaultValue: null,
        description: '',
        name: 'ghostBackground',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      wrapText: {
        defaultValue: {
          value: 'true'
        },
        description: '',
        name: 'wrapText',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      snippet: {
        defaultValue: null,
        description: '',
        name: 'snippet',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        required: false,
        type: {
          name: 'string | string[]'
        }
      }
    },
    extendedInterfaces: ['GuideSection']
  };
} catch (e) {}